import { Station, useGetStations } from "@/entities/station";
import { CommandFilter, FormFieldAutocomplete, FormItem, FormLabel } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useEffect, useMemo, useState } from "react";
import { FieldPath, FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { createStationsRankFunction } from "./stations-filter-scoring";
import { getStationOptions } from "./utils";

interface StationsAutocompleteProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    name: FieldPath<T>;
    className?: string;
    readOnly?: boolean;
    "data-testid"?: string;
    initialValue?: string;
    placeholder?: string;
    label?: string | null;
    showSearchIcon?: boolean;
    nlStationsOnly?: boolean;
    preferredStations?: Station[];
    filterFunction?: (station: Station) => boolean; // Custom filter that overrides nlStationsOnly
}

export function StationsAutocomplete<T extends FieldValues>({
    form,
    name,
    className,
    readOnly,
    label = "Station",
    nlStationsOnly = false,
    preferredStations,
    placeholder,
    showSearchIcon,
    "data-testid": dataTestid,
    initialValue,
    filterFunction,
}: Readonly<StationsAutocompleteProps<T>>) {
    const { data: stations, isPending, error } = useGetStations();

    const includedStations = useMemo(() => {
        if (filterFunction) {
            return stations?.filter(filterFunction) ?? [];
        }

        if (nlStationsOnly) {
            return stations?.filter((s) => s.landCode === "NL") ?? [];
        }

        return stations ?? [];
    }, [stations, nlStationsOnly, filterFunction]);

    const rankFn: CommandFilter | undefined = useMemo(() => {
        if (preferredStations && includedStations) {
            return createStationsRankFunction(preferredStations, includedStations);
        }
        return createStationsRankFunction([], includedStations);
    }, [preferredStations, includedStations]);

    const options = useMemo(() => getStationOptions(includedStations ?? []), [includedStations]);
    const [currentStationName, setCurrentStationName] = useState<string | undefined>();

    useEffect(() => {
        if (options.length && initialValue && !form.getValues(name)) {
            const matchingStation = options.find((station) => station.value.toLowerCase() === initialValue.toLowerCase());
            if (matchingStation) {
                form.setValue(name, matchingStation.value as PathValue<T, Path<T>>);
                setCurrentStationName(matchingStation.label);
            }
        }
    }, [options, initialValue, form, name]);

    if (isPending) {
        return <p>Laden...</p>;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van stations" />;
    }

    if (!stations) {
        return <p>Geen stations gevonden</p>;
    }

    return readOnly ? (
        <FormItem className={className}>
            <FormLabel>{"Station"}</FormLabel>
            <span className="w-full">{currentStationName}</span>
        </FormItem>
    ) : (
        <FormFieldAutocomplete
            form={form}
            label={label === null ? undefined : label}
            name={name}
            placeholder={placeholder ?? "Station"}
            className={className}
            readOnly={readOnly}
            showSearchIcon={showSearchIcon}
            data-testid={dataTestid}
            options={options}
            disableBuiltInFilter={!!preferredStations?.length}
            rankFn={rankFn}
        />
    );
}
